import { CloseOutlineIcon } from '@carboncopyco/icons';
import {
  Button,
  ColorHex,
  InlineLink,
  Modal,
  Separator,
  TextBase,
  notify,
} from '@carboncopyco/ui';
import { getMaxZIndex } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EditWithAIPrompt, onAiRequestError } from '../../EditWithAI';
import { TextTitleWithTooltip } from '../../RulePreview/RulePreview';
import { SearchBoxButton } from '../../SearchBoxButton';
import type { ITextReplaceResult, Rule } from '../types';
import { DataListView } from './DataListView';
import { LoadingAI } from './LoadingAI';
import { NoResultModalSection } from './NoResultModalSection';

export interface AIResultModalProps {
  onClose: () => void;
  open?: boolean;
  prompt?: string;
  setPrompt?: (text: string) => void;
  onRegenerate?: () => Promise<ITextReplaceResult[]>;
  promptGeneratedName: string;
  editRule?: Rule;
  checkIfTheReplaceTextRuleExists: (oldValue: string) => boolean;
  createAiRequest: (data: {
    prompt: string;
    name: string;
    dataList: ITextReplaceResult[];
    modification?: Rule;
  }) => Promise<void>;
  isReadyToGenerate: boolean;
}

export const AIResultModal = ({
  onClose,
  open,
  onRegenerate,
  prompt,
  setPrompt,
  promptGeneratedName,
  createAiRequest,
  editRule,
  checkIfTheReplaceTextRuleExists,
  isReadyToGenerate,
}: AIResultModalProps) => {
  const [dataList, setDataList] = useState<ITextReplaceResult[]>([]);
  const [originalDataList, setOriginalDataList] = useState<
    ITextReplaceResult[]
  >([]);
  const [editItemKey, setEditItemKey] = useState<string>();
  const [generatedPrompt, setGeneratedPrompt] = useState<string>(prompt);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [selectedItems, setSelectedItems] = useState<ITextReplaceResult[]>([]);
  const [editItemKeyError, setEditItemKeyError] = useState<string>('');
  const [isGenerateMode, setIsGenerateMode] = useBoolean(false);
  const [promptError, setPromptError] = useState('');
  const shouldDisableSave = useMemo(() => {
    if (isGenerateMode) {
      return (
        isLoading ||
        !!promptError ||
        !generatedPrompt.length ||
        prompt === generatedPrompt
      );
    }
    return (
      isLoading ||
      !!editItemKeyError.length ||
      !selectedItems.length ||
      selectedItems.some((item) => item.original === editItemKey)
    );
  }, [
    prompt,
    editItemKey,
    editItemKeyError,
    promptError,
    selectedItems,
    isLoading,
    isGenerateMode,
    generatedPrompt,
    selectedItems,
  ]);
  const isEditMode = !!editRule;
  const isNoResultsMode =
    !isLoading &&
    !dataList.length &&
    !isGenerateMode &&
    !originalDataList.length;
  const isNoSearchResults =
    !isLoading && !dataList.length && searchValue && !!originalDataList.length;
  const shouldShowRegenerateButton =
    !isNoResultsMode && !isGenerateMode && !isEditMode;

  const searchText = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (!value) {
        setDataList(originalDataList);
        return;
      }
      const search = value.toLowerCase();
      const filteredData = originalDataList.filter(
        (item) =>
          item.original.toLowerCase().includes(search) ||
          item.replace_with.toLowerCase().includes(search)
      );
      setDataList(filteredData);
    },
    [originalDataList]
  );

  useEffect(() => {
    searchText(searchValue);
  }, [originalDataList]);
  const resetForm = () => {
    setDataList([]);
    setOriginalDataList([]);
    setSearchValue('');
    setSelectedItems([]);
    setEditItemKeyError('');
    setIsGenerateMode.setFalse();
    setEditItemKey('');
    setIsLoading(true);
  };
  const onRegenerateRequest = async () => {
    resetForm();
    let data = undefined;
    try {
      data = await onRegenerate();
      setDataList(data);
      setOriginalDataList(data);
      setSelectedItems(data);
    } catch (e) {
      onAiRequestError(e, 'AI Manager');
    } finally {
      setIsLoading(false);
      setIsGenerateMode.setFalse();
    }
  };

  useEffect(() => {
    if (open && isReadyToGenerate) {
      onRegenerateRequest();
    }
  }, [open, prompt, isReadyToGenerate]);

  const deleteItem = (item: ITextReplaceResult) => {
    const filterOriginal = (list: ITextReplaceResult[]) =>
      list.filter((i) => i.original !== item.original);
    setSelectedItems(filterOriginal(selectedItems));
    setOriginalDataList(filterOriginal(originalDataList));
    searchText(searchValue);
    if (editItemKeyError === item.original) setEditItemKeyError('');
  };

  const editItem = (
    item: ITextReplaceResult,
    data: {
      newOriginalText: string;
      newReplaceText: string;
    }
  ) => {
    const changeList = (list: ITextReplaceResult[]) =>
      list.map((i) => {
        if (i.original === item.original) {
          return {
            ...i,
            original: data.newOriginalText,
            replace_with: data.newReplaceText,
          };
        }
        return i;
      });
    setOriginalDataList(changeList(originalDataList));
    setSelectedItems(changeList(selectedItems));
  };

  const handleSave = async () => {
    if (isGenerateMode) {
      return setPrompt(generatedPrompt);
    }

    setIsLoading(true);
    try {
      await createAiRequest({
        prompt: prompt,
        name: promptGeneratedName,
        dataList: selectedItems as ITextReplaceResult[],
        modification: editRule,
      });
    } catch (_e) {
      notify.error('Failed to save data. Please try again later.');
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    setGeneratedPrompt(prompt);
  }, [prompt]);

  useEffect(() => {
    if (isGenerateMode) setGeneratedPrompt(prompt);
  }, [isGenerateMode]);

  const maxZIndex = useMemo(() => {
    return getMaxZIndex() + 1;
  }, []);

  return (
    open && (
      <Modal
        zIndex={maxZIndex}
        containerClass=" w-[600px]"
        noPadding
        locked
        closeOnlyFromCloseIcon={isNoResultsMode}
        open={open}
        onClose={onClose}
      >
        <div className="flex flex-col p-5 h-[600px] justify-between">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center -m-5 h-full">
              <LoadingAI />
            </div>
          ) : isNoResultsMode ? (
            <NoResultModalSection>
              {editRule ? null : (
                <Button
                  link
                  label="Regenerate the request"
                  onClick={() => setIsGenerateMode.setTrue()}
                />
              )}
            </NoResultModalSection>
          ) : (
            <div className="flex flex-col gap-2 pb-5">
              <div className="flex flex-col gap-2.5">
                <div className="justify-between flex w-full">
                  <div aria-label="ai-rule-title">
                    <TextTitleWithTooltip label={promptGeneratedName} />
                  </div>
                  <div
                    onClick={onClose}
                    className="cursor-pointer"
                  >
                    <CloseOutlineIcon size={20} />
                  </div>
                </div>
                {!isGenerateMode && (
                  <>
                    <div className="flex justify-between w-full ">
                      <TextBase
                        label="View replacement matches from this page only"
                        color="text-ds-natural-600"
                      />
                      <span
                        className="cursor-pointer"
                        onClick={() => {
                          return;
                        }}
                      >
                        <SearchBoxButton
                          buttonColor={ColorHex.primary300}
                          value={searchValue}
                          onChange={searchText}
                        />
                      </span>
                    </div>
                    <div className="flex justify-between w-full select-none">
                      <InlineLink
                        underline
                        disabled={selectedItems.length === dataList.length}
                        as={Button}
                        label="Select all"
                        color="text-ds-natural-600"
                        onClick={() => setSelectedItems(dataList)}
                      />
                      <Button
                        blue
                        link
                        disabled={!selectedItems.length}
                        label="Clear"
                        onClick={() => setSelectedItems([])}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="flex  flex-col w-full">
                {isGenerateMode ? (
                  <EditWithAIPrompt
                    inputLabel="Find and Replace Using AI"
                    setPrompt={setGeneratedPrompt}
                    prompt={generatedPrompt}
                    setPromptError={setPromptError}
                    promptError={promptError}
                  />
                ) : isNoSearchResults ? (
                  <NoResultModalSection noSearchResult={true}>
                    <Button
                      link
                      blue
                      label="Clear"
                      onClick={() => searchText('')}
                    />
                  </NoResultModalSection>
                ) : (
                  <DataListView
                    checkIfTheReplaceTextRuleExists={
                      checkIfTheReplaceTextRuleExists
                    }
                    editItemKey={editItemKey}
                    setEditItemKey={setEditItemKey}
                    editItem={editItem}
                    deleteItem={deleteItem}
                    dataList={dataList}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    editItemErrorKey={editItemKeyError}
                    setEditItemErrorKey={setEditItemKeyError}
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-6 justify-end">
            <Separator horizontal />
            <div className="flex justify-between items-center select-none">
              {isGenerateMode ? (
                <div className="w-[200px]">
                  <Button
                    link
                    label="Back"
                    onClick={() => setIsGenerateMode.setFalse()}
                  />
                </div>
              ) : (
                <TextBase
                  label={`Selected modifications: ${selectedItems.length} out of ${originalDataList.length}`}
                />
              )}

              <div className="flex gap-2">
                <Button
                  secondary
                  label="Discard"
                  link
                  onClick={() => {
                    onClose();
                  }}
                />
                {shouldShowRegenerateButton && (
                  <Button
                    secondary
                    label="Regenerate"
                    disabled={isLoading}
                    onClick={() => {
                      setIsGenerateMode.setTrue();
                    }}
                  />
                )}
                <Button
                  label="Apply"
                  disabled={shouldDisableSave}
                  onClick={handleSave}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  );
};
